import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Trash2, X, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError, fmtCurrency } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// -----------------------------------------------------------------------------
// Duplicate cleanup for depreciations.
// -----------------------------------------------------------------------------
// Groups existing rows by their normalised asset name (case + whitespace + punctuation
// insensitive, matches the AI-import duplicate detector). Per group, the user picks
// exactly one row to keep — the others are deleted via `DELETE /api/depreciations/{id}`.
//
// Default "keep" heuristic: the row with the highest `initial_value` (usually the real
// asset; typos have $0 book values). Falls back to the first row when tied.
// -----------------------------------------------------------------------------

function normName(s) {
  return (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

function pickBestKeeper(group) {
  // Highest initial_value wins; ties broken by highest current_book_value; final tiebreak = first.
  let best = group[0];
  for (const r of group.slice(1)) {
    if ((r.initial_value || 0) > (best.initial_value || 0)) best = r;
    else if (
      (r.initial_value || 0) === (best.initial_value || 0) &&
      (r.current_book_value || 0) > (best.current_book_value || 0)
    ) best = r;
  }
  return best.id;
}

export default function DuplicateCleanupDialog({ open, onOpenChange, clubId, onDone }) {
  const t = useT();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // keepChoice: { [normalised name]: id of the row to keep }
  const [keep, setKeep] = useState({});

  useEffect(() => {
    if (!open || !clubId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/clubs/${clubId}/depreciations`)
      .then(({ data }) => {
        if (cancelled) return;
        setRows(data || []);
      })
      .catch(() => setRows([]))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, clubId]);

  // Group by normalised name; keep only groups with 2+ rows.
  const groups = useMemo(() => {
    const byName = new Map();
    for (const r of rows) {
      const key = normName(r.asset_name);
      if (!key) continue;
      if (!byName.has(key)) byName.set(key, { key, name: r.asset_name, rows: [] });
      byName.get(key).rows.push(r);
    }
    return Array.from(byName.values()).filter((g) => g.rows.length >= 2);
  }, [rows]);

  // Seed default "keep" per group whenever the group set changes.
  useEffect(() => {
    const next = { ...keep };
    let touched = false;
    for (const g of groups) {
      if (!next[g.key] || !g.rows.some((r) => r.id === next[g.key])) {
        next[g.key] = pickBestKeeper(g.rows);
        touched = true;
      }
    }
    if (touched) setKeep(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const toDelete = useMemo(() => {
    const ids = [];
    for (const g of groups) {
      const keepId = keep[g.key];
      for (const r of g.rows) if (r.id !== keepId) ids.push(r.id);
    }
    return ids;
  }, [groups, keep]);

  const close = () => {
    onOpenChange?.(false);
    setKeep({});
  };

  const doCleanup = async () => {
    if (toDelete.length === 0) return;
    if (!window.confirm(
      t("Weet je zeker dat je {n} dubbele activa wilt verwijderen? Dit kan niet ongedaan gemaakt worden.")
        .replace("{n}", String(toDelete.length))
    )) return;
    setBusy(true);
    let deleted = 0;
    let failed = 0;
    for (const id of toDelete) {
      try {
        await api.delete(`/depreciations/${id}`);
        deleted += 1;
      } catch {
        failed += 1;
      }
    }
    setBusy(false);
    if (failed > 0) {
      toast.error(
        t("{d} verwijderd, {f} mislukt").replace("{d}", String(deleted)).replace("{f}", String(failed))
      );
    } else {
      toast.success(t("{d} duplicaten opgeruimd").replace("{d}", String(deleted)));
    }
    onDone?.();
    close();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange?.(true) : close())}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-5 border-b-2 border-foreground">
          <DialogTitle className="font-display uppercase text-2xl tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-primary" />
            {t("Duplicaten opruimen")}
          </DialogTitle>
          <DialogDescription>
            {t("Vind rijen met dezelfde activum-naam en kies per groep welke je wilt behouden. De niet-geselecteerde rijen worden verwijderd.")}
          </DialogDescription>
        </DialogHeader>

        <div className="p-5 flex-1 overflow-auto" data-testid="dup-cleanup-body">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> {t("Laden…")}
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-10">
              <CheckCircle2 className="w-8 h-8 mx-auto text-positive mb-2" />
              <p className="font-display uppercase text-lg tracking-tight">
                {t("Geen duplicaten gevonden")}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t("Alle activa-namen zijn uniek — je bent klaar.")}
              </p>
            </div>
          ) : (
            <div className="space-y-5" data-testid="dup-cleanup-groups">
              <div className="stat-label">
                {t("{g} groep(en) met duplicaten · {n} rij(en) te verwijderen")
                  .replace("{g}", String(groups.length))
                  .replace("{n}", String(toDelete.length))}
              </div>
              {groups.map((g) => (
                <div key={g.key} className="border-2 border-foreground bg-card" data-testid={`dup-group-${g.key}`}>
                  <div className="px-3 py-2 bg-muted/50 border-b-2 border-foreground flex items-center justify-between">
                    <span className="font-display uppercase font-bold tracking-tight">
                      {g.name}{" "}
                      <span className="text-muted-foreground text-xs">
                        · {g.rows.length} {t("rijen")}
                      </span>
                    </span>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-muted-foreground border-b border-foreground/20">
                        <th className="px-3 py-1.5 w-16 text-center">{t("Behouden")}</th>
                        <th className="px-3 py-1.5 text-left">{t("Verkregen")}</th>
                        <th className="px-3 py-1.5 text-left">{t("Startdatum")}</th>
                        <th className="px-3 py-1.5 text-right">{t("Initieel")}</th>
                        <th className="px-3 py-1.5 text-right">{t("Boekwaarde")}</th>
                        <th className="px-3 py-1.5 text-right">{t("Restwaarde")}</th>
                        <th className="px-3 py-1.5 text-center">{t("Actief?")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.rows.map((r) => {
                        const isKept = keep[g.key] === r.id;
                        return (
                          <tr
                            key={r.id}
                            className={isKept ? "bg-primary/10" : "opacity-70"}
                            data-testid={`dup-row-${r.id}`}
                          >
                            <td className="px-3 py-2 text-center">
                              <input
                                type="radio"
                                name={`keep-${g.key}`}
                                checked={isKept}
                                onChange={() => setKeep((c) => ({ ...c, [g.key]: r.id }))}
                                data-testid={`dup-keep-${r.id}`}
                                className="w-4 h-4 cursor-pointer"
                              />
                            </td>
                            <td className="px-3 py-2 font-mono-num">{r.acquired_year || "—"}</td>
                            <td className="px-3 py-2 font-mono-num text-xs">{r.start_date || "—"}</td>
                            <td className="px-3 py-2 font-mono-num text-right">{fmtCurrency(r.initial_value)}</td>
                            <td className="px-3 py-2 font-mono-num text-right">{fmtCurrency(r.current_book_value)}</td>
                            <td className="px-3 py-2 font-mono-num text-right">
                              {r.residual_value ? fmtCurrency(r.residual_value) : "—"}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {r.is_active === false ? t("Nee") : t("Ja")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="p-5 border-t-2 border-foreground flex-shrink-0">
          <button
            onClick={close}
            data-testid="dup-cleanup-cancel"
            className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold tracking-wider inline-flex items-center gap-2"
          >
            <X className="w-4 h-4" /> {t("Annuleren")}
          </button>
          {groups.length > 0 && (
            <button
              onClick={doCleanup}
              disabled={busy || toDelete.length === 0}
              data-testid="dup-cleanup-execute"
              className="btn-brutal bg-destructive text-white px-5 py-2 font-display uppercase font-bold tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              {busy
                ? t("Bezig…")
                : t("Verwijder {n} rij(en)").replace("{n}", String(toDelete.length))}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
